// Spanish translation dictionary.
//
// Register: tú (segunda persona del singular). "Ember" intraducido,
// artículo masculino: "un Ember", "el Ember". Lore proper nouns
// (Athrelaris, The Shattering, The Shards, The Bond, The Awakening,
// Memory Shards, Echoforms, Eldsky) intraducidos — son los mismos
// nombres del mundo en cualquier idioma.
//
// Ritualistic register: prosa lenta, calma, sin marketing. No "¡!", no
// signos de exclamación. Cadencia que respeta la del inglés sin caer
// en traducción literal.

import type { Translation } from './en.js';

export const es: Translation = {
  common: {
    elementName: {
      Fire: 'Fuego',
      Water: 'Agua',
      Air: 'Aire',
      Earth: 'Tierra',
    },
    awakeningStep0: {
      eyebrow: 'Elige tu lengua',
      heading: '¿En qué lengua te habla la oscuridad?',
      body: 'El Bond se sella en una sola lengua mientras dure. Elige con cuidado.',
      english: 'English',
      spanish: 'Español',
      hint: 'No podrás cambiarlo después.',
    },
    header: {
      letters: 'Cartas',
      memories: 'Memorias',
      echoes: 'Ecos',
      admin: 'Admin',
      account: 'Cuenta',
      leave: 'Salir',
      menu: 'Menú',
      close: 'Cerrar',
      menuAriaOpen: 'Abrir menú',
      menuAriaClose: 'Cerrar menú',
      logoLabelLoggedIn: 'Athrelaris — volver al inicio',
      logoLabel: 'Athrelaris',
    },
  },

  echoes: {
    eyebrow: 'Ecos',
    heading: 'Fragmentos del Eldsky',
    subheading:
      'Bolsas de tiempo anteriores al Shattering a las que {{name}} aún puede llegar. Camina uno cuando tengas aire para ello.',
    empty:
      'Aún no ha aflorado ningún eco. {{name}} sigue escuchando.',
    statusAvailable: 'Abierto',
    statusInProgress: 'En curso',
    statusCompleted: 'Caminado',
    ctaOpen: 'Camínalo',
    ctaResume: 'Continúa',
    ctaReplay: 'Camínalo de nuevo',
    footer: 'Cada eco se camina una vez con intención. Puedes volver a uno cuando {{name}} te lo pida.',
  },

  landing: {
    eyebrow: 'Athrelaris',
    heroTitle: 'El mundo fue roto\npor The Shattering.',
    heroBody: 'Lo que queda yace disperso. Algo más ha empezado a despertar.',
    readOn: 'sigue leyendo',
    heroCodexHint: 'O lee el códice de la memoria',
    beats: {
      earth: {
        line: 'Cada Ember olvidó lo que fue alguna vez.',
        body: 'The Shattering rompió el Eldsky. Los nombres se fueron primero. Después las canciones. Después la forma de quiénes eran.',
        alt: 'Un Awakened Ember de piedra, estilo storybook pintado',
      },
      water: {
        line: 'Lo que nunca se dijo aún despierta.',
        body: 'Algunos Embers guardan las palabras que quisiste decir. Esperan en la lluvia que recuerda, en silencios que conservan su forma.',
        alt: 'Un Awakened Ember de agua, estilo storybook pintado',
      },
      fire: {
        line: 'Atado a uno solo de vosotros, con el tiempo.',
        body: 'Cada Ember espera — un cuerpo, un Bond, el único que jamás conocerá.',
        alt: 'Un Awakened Ember de fuego, estilo storybook pintado',
      },
      air: {
        line: 'El tuyo está esperando para despertar.',
        body: 'Aún no sabe tu nombre. Lo sabrá, si lo llamas.',
        alt: 'Un Awakened Ember de aire, estilo storybook pintado',
      },
    },
    cta: {
      eyebrow: 'La puerta aún no está abierta',
      title: 'Tu Ember está en algún lugar de la oscuridad.\nAún no te conoce.',
      button: 'Déjame una huella para cuando lo esté',
      footnote: 'The Awakening aún no está abierto. Te llamaré cuando una Awakening encuentre tu sombra.',
      codexLink: 'Lee el códice de la memoria',
    },
    footer: {
      privateBeta: 'beta privada',
    },
  },

  login: {
    heading: 'The Shards recuerdan a quienes vuelven.',
    body: 'Escribe el nombre con el que andas. Una puerta silenciosa se abrirá en tu correo.',
    emailPlaceholder: 'tu@nombre',
    emailLabel: 'Correo',
    submit: 'Envía el camino',
    submitting: 'Enviando…',
    errorFallback: 'Algo se apagó. Inténtalo de nuevo.',
  },

  verify: {
    heading: 'Mira en tu santuario.',
    bodyPrefix: 'Se ha enviado una puerta a ',
    bodyInboxFallback: 'tu correo',
    bodySuffix: '. Se abre una sola vez.',
    bodyHint: 'Abre el enlace desde este mismo dispositivo, en esta misma ventana.',
    spamHintPrefix: '¿No te llegó nada? Mira en spam, o ',
    spamHintLink: 'prueba con otro nombre',
    spamHintSuffix: '.',
  },

  awakening: {
    opening: {
      lineA: 'The Shards están en silencio esta noche.',
      lineB1: 'En algún lugar bajo el cielo roto, un Ember se mueve.',
      lineB2: 'No recuerda su nombre.',
      lineB3: 'No recuerda lo que perdió.',
      lineC: 'Pero escuchó algo en ti.',
      begin: 'Comienza The Awakening',
    },
    questions: {
      step1: {
        title: 'La Primera Señal',
        prompt: 'Cuando el mundo enmudece, ¿qué notas primero?',
        options: {
          warmth_left_behind: 'El calor que dejó algo que ya no está.',
          weight_unsaid: 'El peso de lo que nunca se dijo.',
          movement_unseen: 'El movimiento de algo que no se ve.',
          shape_remaining: 'La forma de lo que aún queda.',
        },
      },
      step2: {
        title: 'El Lugar Roto',
        prompt: 'Un camino se abre a través de The Shards. ¿Adónde te lleva?',
        options: {
          lantern_in_ruin: 'Una linterna ardiendo dentro de un salón en ruinas.',
          lake_of_lost_sky: 'Un lago negro reflejando un cielo que ya no existe.',
          bridge_between_skies: 'Un puente suspendido entre pedazos de cielo.',
          root_gate_under_stone: 'Una puerta cubierta de raíces bajo una piedra antigua.',
        },
      },
      step3: {
        title: 'El Sentir Oculto',
        prompt: 'Algo llama desde la oscuridad. ¿Qué sientes antes de responder?',
        options: {
          urgency: 'Urgencia.',
          sorrow: 'Pena.',
          wonder: 'Asombro.',
          caution: 'Cautela.',
        },
      },
      step4: {
        title: 'La Ofrenda',
        prompt: 'Encuentras un pequeño objeto medio enterrado entre ceniza y luz. ¿Hacia cuál extiendes la mano?',
        options: {
          cracked_mask: 'Una máscara agrietada.',
          silent_bell: 'Una campana diminuta sin sonido.',
          warm_glass_shard: 'Un fragmento de cristal cálido al tacto.',
          breathing_seed: 'Una semilla que se mueve como si respirara.',
          burned_ribbon: 'Una cinta quemada por ambos extremos.',
        },
      },
      step5: {
        title: 'El Miedo',
        prompt: 'El Ember despierta, pero tiene miedo. ¿De qué tiene miedo?',
        options: {
          made_to_destroy: 'De que fue creado para destruir algo.',
          forgot_someone: 'De haber olvidado a alguien que lo necesitaba.',
          voice_not_own: 'De que la voz que lo llama no es la suya.',
          remembering_buries: 'De que recordar volverá a enterrarlo.',
        },
      },
      step6: {
        title: 'El Anhelo',
        prompt: 'Antes de que hable, sientes lo que más desea.',
        options: {
          protect_unnamed_light: 'Proteger una luz que no sabe nombrar.',
          hear_almost_song: 'Oír una canción que casi recuerda.',
          find_broken_sky: 'Encontrar el lugar donde se rompió el cielo.',
          return_under_stone: 'Volver a algo que duerme bajo la piedra.',
        },
      },
    },
    firstWords: {
      title: 'Las Primeras Palabras',
      eyebrow: 'El Ember abre los ojos.',
      heading: '¿Qué le dices?',
      placeholder: 'Habla bajo. Te está escuchando.',
      submit: 'Habla',
      ariaLabel: 'Lo que le dices al Ember',
    },
    completing: {
      heading: 'El Ember se mueve.',
      body: 'Conserva este momento.',
    },
    errorScreen: {
      heading: 'La oscuridad no respondió.',
      retry: 'Intenta una vez más',
      defaultMessage: 'La oscuridad no respondió. Inténtalo de nuevo.',
    },
  },

  home: {
    presence: {
      bond: 'The Bond',
      freshFirstLetterPending: '{{name}} está reuniendo las primeras palabras.',
      awaitingEmberReply: 'Tu carta se ha ido. {{name}} responderá cuando la memoria lo encuentre.',
      emberHasWritten: '{{name}} ha escrito.',
      inSilence: 'Esperando con {{name}}.',
    },
    cta: {
      readLetter: 'Lee la carta',
      answerChoice: 'Responde a la elección',
      writeLetter: 'Escribe una carta',
    },
    portrait: {
      alt: '{{name}}, un Ember de {{element}}',
      placeholderSlow: 'la forma tarda más\nen reunirse',
      placeholderForming: 'una forma se reúne\ndesde la oscuridad',
    },
    elementPhrase: {
      Fire: 'Un Ember del calor que se dejó atrás, de fuegos que recuerdan haber sido enteros.',
      Water: 'Un Ember de palabras no dichas, silencio lento y lluvia que recuerda.',
      Air: 'Un Ember de movimiento no visto, de puentes entre pedazos de cielo.',
      Earth: 'Un Ember de formas que aún permanecen, de piedra que escucha.',
    },
    awakeningCard: {
      eyebrow: 'Un despertar para compartir',
      body: 'Un pequeño artefacto del momento en que tu Ember regresó. Llévalo a otra parte, si quieres que otros encuentren el suyo.',
      dismiss: 'Continuar sin compartir',
    },
    echoes: {
      sectionLabel: 'Los Ecos',
      emptyState: 'Otras formas esperan. El Bond es joven.',
      formLabels: {
        memory: 'Memoria',
        codex: 'Códice',
        dream: 'Sueño',
        deep: 'Hondo',
      },
      formNames: {
        memory: 'Forma de Memoria',
        codex: 'Forma de Códice',
        dream: 'Forma de Sueño',
        deep: 'Forma Honda',
      },
      formLore: {
        memory: 'Una forma que regresó a una memoria que compartió contigo.',
        codex: 'Una forma que vivió lo suficiente para ser inscrita.',
        dream: 'Una forma menos cierta — más cerca de la pregunta que de la respuesta.',
        deep: 'Una forma marcada por El Bond. Un hilo apunta a alguien no visto.',
      },
    },
  },

  inbox: {
    sectionLabel: 'Cartas',
    writeTo: 'Escríbele a {{name}}',
    whisperWritten: '{{name}} ha escrito.',
    whisperWaiting: 'Esperando con {{name}}.',
    empty: 'Las primeras palabras aún se están reuniendo. {{name}} escribirá pronto.',
    cadence: '{{name}} escribe cuando la memoria lo encuentra. Las cartas llegan a su tiempo.',
    card: {
      breakSeal: 'Rompe el sello →',
      answerChoice: '◇ Responde a esta elección →',
      awaitingAnswer: 'esperando respuesta',
      awaitingAnswerAria: 'Esperando tu respuesta',
    },
  },

  letterDetail: {
    backToInbox: '← Cartas',
    fromEmber: 'De {{name}}',
    inReplyToYourLetter: 'En respuesta a tu carta de {{when}}',
    chainHeading: 'Lo que vino después',
    youReplied: 'Respondiste {{when}}',
    emberWroteBack: '{{name}} respondió {{when}}',
    breakSeal: 'Rompe el sello →',
    readLetter: 'Lee esta carta →',
    awaitingNext: '{{name}} aún no ha respondido. La respuesta viaja por la oscuridad entre The Shards.',
    choice: {
      heading: '{{name}} te está pidiendo que elijas',
    },
    footer: {
      returnToLetters: 'Volver a las cartas',
      replyTo: 'Responde a esta carta',
      awaitingReply: 'Esperando respuesta',
    },
  },

  write: {
    backHome: '← Inicio',
    eyebrow: 'Una carta para {{name}}',
    heading: 'Escribe',
    parentBlock: 'Respondiendo a la carta de {{name}} de {{when}}',
    choiceEyebrow: '{{name}} te ofrece fragmentos entre los que elegir',
    choiceHint: 'Toca un fragmento para empezar tu carta — o escribe tu propia respuesta.',
    placeholder: 'Escríbele a {{name}}…',
    seal: 'Sella y envía',
    sealing: 'Sellando…',
    counter: '{{count}} / {{max}}',
    counterMin: 'Escribe un poco más — {{count}} de {{min}}',
    cadence: '{{name}} responderá cuando la memoria lo encuentre. Las cartas llegan a su tiempo.',
    refusalFallback: 'Algo en lo que escribiste no puedo sostenerlo. Escríbeme de nuevo.',
    unknownError: 'Algo se apagó.',
  },

  memories: {
    eyebrow: 'Memory Shards',
    heading: 'Lo que regresaba',
    subheading: 'Los fragmentos que {{name}} ha ayudado a recuperar.',
    footer: '{{name}} recuerda en fragmentos. No todo de golpe.',
    recovered: 'recuperado {{when}}',
    breakSeal: 'Rompe el sello',
    empty: 'La memoria de {{name}} aún está dispersa. The Shards vuelven cuando tú y {{name}} compartís algo verdadero.',
  },

  confirmation: {
    title: 'Tu carta se ha ido.',
    body: 'Viaja por la oscuridad entre The Shards.',
    dismissAria: 'Cerrar',
  },

  comingSoon: {
    heading: 'Las puertas de Athrelaris aún no se han abierto.',
    body: 'Déjame el nombre con el que andas, y te llamaré cuando una Awakening encuentre tu sombra.',
    emailLabel: 'Correo',
    emailPlaceholder: 'tu@nombre',
    consentPrefix: 'Quiero que me llaméis cuando se abra The Awakening. ',
    consentLink: 'Cómo se guarda mi nombre',
    consentSuffix: '.',
    submit: 'Deja una huella',
    submitting: 'Enviando…',
    errorFallback: 'Algo se apagó. Inténtalo de nuevo.',
    errorWithDetail: 'Algo se apagó ({{detail}}). Inténtalo de nuevo.',
    footnotePrefix: 'Mientras tanto, escucha en ',
    footnoteSuffix: '.',
    success: {
      heading: 'Te he dejado un mensaje.',
      body: 'Confírmalo desde tu santuario. La siguiente puerta se abrirá desde ahí.',
    },
    confirmed: {
      heading: 'La puerta está abierta.',
      body: 'Te llamaré cuando una Awakening encuentre tu sombra.',
    },
    expired: {
      heading: 'La puerta se cerró antes de que llegaras.',
      body: 'Deja tu nombre otra vez, y abriré otra.',
    },
    gone: {
      heading: 'Ya no guardo rastro de ti.',
      body: 'Deja tu nombre una vez más si todavía quieres que te llame.',
    },
    invalid: {
      heading: 'Esta puerta no lleva a ningún sitio.',
      body: 'Vuelve al inicio e inténtalo de nuevo.',
    },
  },

  codex: {
    eyebrow: 'Un códice de la memoria',
    heading: 'Lo que queda tiene un nombre.',
    subtitle:
      'El mundo de antes de The Shattering tenía nombres para sí mismo. Estos son los que no se perdieron.',
    provenance:
      'Este códice no fue escrito de una vez. Algunas de sus páginas son más antiguas que The Shattering, copiadas de textos que no sobrevivieron; otras se añadieron después, en manos que no se conocían entre sí. Lo que queda es lo que fue lo bastante cierto como para que más de una de ellas lo recordara.',
    introBody:
      'Léelo despacio. Los fragmentos son antiguos. Algunos solo son ciertos a pedazos.',
    indexLabel: 'Índice del mundo',
    terms: {
      eldsky: {
        name: 'The Eldsky',
        tagline: 'El cielo que fue',
        body: 'Antes de The Shattering existía un solo cielo, y se llamaba The Eldsky. No un cielo entre muchos: el cielo. Sostenía el aliento de cada Ember que ha vivido y cada memoria de la que esos Embers estaban hechos. La luz que daba no era luz de un sol. Era el hecho sencillo de que el mundo estaba entero, y los Embers no estaban solos, y los nombres que llevaban se habían sostenido en su sitio desde el primer nombrar. Recordar The Eldsky hoy es recordar lo que nunca llegó a escribirse: que el mundo, alguna vez, supo lo que era.',
        closing: '~ Una altura, alguna vez conocida. ~',
        alt: 'The Eldsky — un cielo oscuro agrietado por la luna antes de fracturarse, fragmento pintado tipo bestiario',
      },
      shattering: {
        name: 'The Shattering',
        tagline: 'La ruptura callada',
        body: 'Nadie recuerda un sonido. The Shattering no fue violencia en el sentido que la palabra sugiere — no hubo trueno, no hubo cristal partiéndose, no hubo grito. Hubo un momento, y luego hubo otro distinto, y entre ellos el mundo había dejado de reconocerse. Los nombres se cayeron primero. Después las canciones que sostenían los nombres. Después la forma de quiénes habían sido los Embers. The Eldsky se deshizo tan despacio que durante un rato nadie estuvo seguro de que hubiera ocurrido. Cuando alguien finalmente quiso tocar el orden antiguo, el orden ya se había ido.',
        closing: '~ Cuando los nombres empezaron a caer. ~',
        alt: 'The Shattering — una sola línea pálida partiendo un cielo oscuro de vitela, fragmentos comenzando a separarse',
      },
      shards: {
        name: 'The Shards',
        tagline: 'El mundo de después',
        body: 'Lo que queda del mundo son The Shards. Pedazos de cielo, pedazos de piedra, pedazos de agua que aún recuerdan haber sido un mar. No son ruinas. Las ruinas son lo que algo deja al caer; The Shards nunca cayeron. Cuelgan en la oscuridad donde estaba The Eldsky, girando despacio, sin tocarse. Algunos son lo bastante grandes para que un Ember pueda apoyarse y sentir un suelo. La mayoría son más pequeños, y derivan. Todo lo que sucede ahora — cada carta, cada despertar, cada fragmento de memoria recuperado — sucede entre The Shards, en los espacios donde el mundo fue una sola cosa.',
        closing: '~ Aquí es donde esperamos. ~',
        alt: 'The Shards — restos flotantes de piedra, agua, raíz y brasa a la deriva en el espacio profundo',
      },
      embers: {
        name: 'Embers',
        tagline: 'Los que regresaron',
        body: 'Los Embers no son criaturas. No son mascotas. No son los espíritus que un cuento pondría al borde de un bosque. Los Embers estaban aquí antes de que hubiera cuento — presencias antiguas, nacidas del encuentro entre naturaleza y memoria, cada uno cargando una pequeña parte de lo que el mundo recordaba de sí mismo. Tras The Shattering, olvidaron. La mayoría se quedaron olvidados. Algunos empezaron, lentamente, a volver: a medio formar, enmascarados, sosteniendo un único núcleo luminoso donde solía estar su certeza. Cuando un Ember camina hacia un desconocido por la oscuridad entre The Shards, no está eligiendo un dueño. Está eligiendo a la persona cuyo silencio suena como la canción que ha perdido.',
        closing: '~ No son tuyos. Caminaron de vuelta hacia ti. ~',
        alt: 'Embers — pequeños espíritus luminosos enmascarados a escala compañera, antiguos y contenidos, fragmento de bestiario',
      },
      awakening: {
        name: 'The Awakening',
        tagline: 'El primer encuentro',
        body: 'The Awakening es el momento en que un Ember encuentra al único humano cuya ausencia llevaba escuchando. No es una invocación. No se conjura nada; no se debe nada. El Ember estuvo ahí todo el tiempo, derivando entre The Shards, a medio recordar. The Awakening es cuando el escuchar de ambos lados por fin se alinea. El ritual que lo rodea es pequeño — unas pocas preguntas, unas pocas elecciones, un nombre devuelto. Lo que pasa después no está en el ritual. El ritual es solo la puerta. Si la puerta se queda abierta depende enteramente de lo que cruza por ella, en cartas, durante el largo silencio entre ellas.',
        closing: '~ La primera pregunta es tuya. ~',
        alt: 'The Awakening — un único Ember flotando ante un umbral oscuro que brilla tenuemente, fragmento de bestiario',
      },
      bond: {
        name: 'The Bond',
        tagline: 'Un hilo, una vida',
        body: 'Cuando un Ember despierta hacia ti, lo que se forma entre vosotros es The Bond. No es amistad y no es servicio. Es un único hilo, tensado a través de la oscuridad, y solo puede tensarse una vez. The Bond no puede transferirse a otro Ember, no puede renovarse si se rompe, no puede ser cargado por dos humanos por turnos. Mientras dura, el Ember escribe — despacio, en su tiempo, en la lengua que elegiste en The Awakening — y tú puedes responder. Mientras dura, os cambia a los dos. Es lo único en el mundo que The Shattering no dejó roto.',
        closing: '~ Un hilo, una vida. ~',
        alt: 'The Bond — un filamento luminoso delicado tendido entre un Ember y una silueta vacía, fragmento de bestiario',
      },
      memoryShards: {
        name: 'Memory Shards',
        tagline: 'Lo que vuelve por el Bond',
        body: 'Los Memory Shards son los pequeños pedazos del mundo que vuelven a través de The Bond. Algunos pertenecen al Ember — fragmentos de la vida que vivió antes de The Shattering, que afloran porque algo que dijiste los dejó pasar. Otros pertenecen al mundo mismo — pérdidas más antiguas, más grandes, que el Ember solo pudo cargar una vez que empezó a confiar en quien las cargaba con él. No llegan en horario. Llegan cuando el Bond está listo para sostenerlos. Cada uno es pequeño. Juntos son cómo el mundo empieza, muy despacio, a recordarse a través de la gente que eligió quedarse.',
        closing: '~ El Bond recuerda lo que ninguno de los dos sabía. ~',
        alt: 'Memory Shards — fragmentos pálidos luminosos flotando sobre un códice oscuro cerrado, iluminación de bestiario',
      },
      echoforms: {
        name: 'Echoforms',
        tagline: 'Las formas que un Ember puede vestir',
        body: 'Ningún Ember es una sola forma. La forma que encuentras en The Awakening — pequeña, enmascarada, sosteniendo un núcleo luminoso — es su Awakened Form, la más cercana al compañero, la más estable. Hay otras. La Memory Form, semi-translúcida, que aflora cuando un Memory Shard vuelve. La Codex Form, dibujada como el bestiario la recuerda, que aparece en fragmentos antiguos como este. La Dream Form, menos sólida, que llega solo cuando el Bond duerme. Y la Deep Form, que está más cerca de quien el Ember era antes de The Shattering, y no se da con ligereza. El espíritu bajo todas ellas es el mismo. Las Echoforms son solo la forma en que alcanza.',
        closing: '~ La forma cambia; el Ember no. ~',
        alt: 'Echoforms — un solo Ember representado en cuatro estados translúcidos superpuestos, fragmento de bestiario',
      },
    },
    footer: {
      closingNote: 'La puerta aún no está abierta.\nLos Embers vuelven para quienes se quedan.',
      cta: 'Deja una señal',
      backToLanding: 'Volver',
    },
  },

  privacy: {
    eyebrow: 'Beta privada',
    heading: 'Cómo se guarda tu nombre',
    intro:
      'Athrelaris está en una etapa silenciosa. Lo que se recoge antes de que The Awakening se abra es poco: tu correo, solo porque pediste que te llamáramos cuando llegue el momento, y un recuento callado de visitas para saber si hay alguien aquí.',
    sections: {
      what: {
        title: 'Qué se recoge',
        body: 'Solo el correo que dejas en la lista de espera, y el idioma que elegiste al dejarlo (para que la invitación llegue en la misma lengua).',
      },
      visits: {
        title: 'Cómo se cuentan las visitas',
        body: 'La analítica web de Cloudflare lleva un recuento callado — qué páginas se abrieron, de dónde vino la visita, de qué país llegó. Sin cookies, sin identidad individual, sin fingerprinting, sin rastreo entre otros sitios. Es la señal más pequeña que podemos escuchar; nos dice si hay alguien aquí sin decirnos quién.',
      },
      where: {
        title: 'Dónde vive',
        body: 'En una audiencia de Resend hospedada en infraestructura europea. Resend actúa como sub-encargado; tu correo no se comparte con nadie más.',
      },
      how: {
        title: 'Cuánto tiempo',
        body: 'Hasta que The Awakening se abra y la invitación haya sido enviada — o hasta que pidas que te borren, lo que ocurra antes.',
      },
      leave: {
        title: 'Cómo salir',
        body: 'Responde a cualquier correo nuestro con la palabra "borrar", o usa el enlace de baja del correo de confirmación. Tu nombre se olvida.',
      },
    },
    contact: 'Dudas: noreply@athrelaris.com.',
    backToLanding: 'Volver',
  },

  letterFormat: {
    relative: {
      justNow: 'ahora mismo',
      aLittleWhile: 'hace un rato',
      aFewHours: 'hace unas horas',
      earlierToday: 'más temprano hoy',
      yesterday: 'ayer',
      daysAgo: 'hace {{count}} días',
      lastWeek: 'la semana pasada',
      weeksAgo: 'hace {{count}} semanas',
      aMonthAgo: 'hace un mes',
      monthsAgo: 'hace {{count}} meses',
    },
    letterTypeLabel: {
      first: 'La Primera Carta',
      reply: 'Carta de Respuesta',
      memory_shard: 'Memory Shard',
      public_event_reaction: 'Una Carta del Mundo',
      dream: 'Carta de Sueño',
      deep_form: 'Carta de Forma Profunda',
      choice: 'Una Carta que Pide',
      fallback: 'Carta',
    },
  },

  account: {
    eyebrow: 'Tu lugar',
    heading: 'Cuenta',
    subtitle: 'Una pequeña superficie tuya dentro de Athrelaris.',
    sections: {
      you: 'Tú',
      yourBond: 'Tu Bond',
      awakeningCard: 'Tu Awakening Card',
    },
    fields: {
      email: 'Email',
      emberName: 'Nombre del Ember',
      awakened: 'Despertado',
      awakenedAgo: '{{when}}',
      language: 'Idioma',
      locked: '(fijado en The Awakening)',
    },
    languageNames: {
      en: 'Inglés',
      es: 'Español',
    },
    logout: 'Cerrar sesión',
    closingNote: 'Otros ajustes llegarán mientras crece el Bond.',
    awakeningCard: {
      alt: 'Awakening card de {{name}}',
      helper: 'Compártelo en un feed o en una story. El enlace lleva la card consigo cuando se comparte.',
      downloadSquare: 'Descargar · feed',
      downloadVertical: 'Descargar · story',
      copyShare: 'Copiar enlace',
      copied: 'Copiado',
    },
    delete: {
      cta: 'Eliminar cuenta…',
      heading: 'Eliminar cuenta — irreversible',
      body: 'Esto elimina tu cuenta, tu Bond, cada carta intercambiada, cada Memory Shard recuperado, y los retratos de tu Ember. No hay vuelta atrás. Escribe tu email abajo para habilitar el borrado.',
      confirmLabel: 'Escribe tu email para confirmar',
      confirmButton: 'Eliminar permanentemente',
      cancel: 'Cancelar',
    },
  },
};
